import { ReactNode, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, Wrench, ChevronDown, ChevronRight,
  User as UserIcon, LogOut, Bell, Menu, Store, ShoppingBag, Settings,
  MessageSquare, DollarSign, Megaphone, Calendar, TrendingUp, Heart,
  Link2, Users as UsersIcon, ClipboardList,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { LayoutContextProvider, useLayoutCtx } from "./LayoutContext";

interface NavItem {
  label: string;
  href?: string;
  icon: typeof LayoutDashboard;
  children?: { label: string; href: string }[];
}

const baseNavItems: NavItem[] = [
  { label: "Overview", href: "/dashboard", icon: LayoutDashboard },
  {
    label: "Marketplace",
    icon: Store,
    children: [
      { label: "Goods", href: "/dashboard/goods" },
      { label: "Services", href: "/dashboard/services" },
    ],
  },
  { label: "Leads", href: "/dashboard/leads", icon: MessageSquare },
  { label: "Sales", href: "/dashboard/sales", icon: DollarSign },
  { label: "Promotions", href: "/dashboard/promotions", icon: Megaphone },
  { label: "Investments", href: "/dashboard/investments", icon: TrendingUp },
];

const fundraiseNavItem: NavItem = {
  label: "Fundraise",
  icon: Heart,
  children: [
    { label: "My Campaigns", href: "/fundraiser/dashboard" },
    { label: "Donations", href: "/fundraiser/donations" },
  ],
};

const affiliateNavItem: NavItem = {
  label: "Affiliate",
  icon: UsersIcon,
  children: [
    { label: "Registrations", href: "/affiliate/registrations" },
    { label: "Link", href: "/affiliate/link" },
  ],
};

const tailNavItems: NavItem[] = [
  { label: "Profile", href: "/dashboard/profile", icon: UserIcon },
  { label: "Settings", href: "/dashboard/settings", icon: Settings },
];

// Icon shown next to a dropdown child link (children only carry label + href).
const childIcon = (label: string) => {
  switch (label) {
    case "Goods": return <ShoppingBag size={14} />;
    case "Registrations": return <ClipboardList size={14} />;
    case "Events": return <Calendar size={14} />;
    case "Link": return <Link2 size={14} />;
    default: return <Wrench size={14} />;
  }
};

export function MemberLayoutChrome({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();

  const navItems: NavItem[] = [
    ...baseNavItems,
    ...((user?.can_crowdfund || user?.role === "admin") ? [fundraiseNavItem] : []),
    ...(user?.is_affiliate ? [affiliateNavItem] : []),
    ...tailNavItems,
  ];
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>("Marketplace");

  const isActive = (href: string) => location.pathname === href;
  const isChildActive = (children?: { href: string }[]) => children?.some(c => isActive(c.href));

  const handleLogout = () => { logout(); navigate("/login"); };

  const Sidebar = () => (
    <div className="flex flex-col h-full bg-navy">
      <div className="flex items-center px-5 h-16 border-b border-white/10 shrink-0">
        <span className="text-white font-semibold text-sm">Member Portal</span>
      </div>

      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          if (item.children) {
            const open = openDropdown === item.label;
            const childActive = isChildActive(item.children);
            return (
              <div key={item.label}>
                <button
                  onClick={() => setOpenDropdown(open ? null : item.label)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    childActive ? "bg-white/10 text-white" : "text-white/70 hover:text-white hover:bg-white/5"
                  }`}
                >
                  <item.icon size={18} className="shrink-0" />
                  <span className="flex-1 text-left">{item.label}</span>
                  {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
                <AnimatePresence>
                  {open && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="ml-3 mt-1 pl-4 border-l border-white/10 space-y-0.5 py-1">
                        {item.children.map((child) => (
                          <Link
                            key={child.href}
                            to={child.href}
                            onClick={() => setMobileOpen(false)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-md text-[13px] transition-colors ${
                              isActive(child.href)
                                ? "bg-gold/15 text-gold font-medium"
                                : "text-white/60 hover:text-white hover:bg-white/5"
                            }`}
                          >
                            {childIcon(child.label)}
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          }
          return (
            <Link
              key={item.label}
              to={item.href!}
              onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                isActive(item.href!) ? "bg-white/10 text-white" : "text-white/70 hover:text-white hover:bg-white/5"
              }`}
            >
              <item.icon size={18} className="shrink-0" />
              {item.label}
            </Link>
          );
        })}
      </nav>

      <div className="border-t border-white/10 p-3 shrink-0 space-y-1">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center shrink-0">
            <span className="text-gold text-xs font-bold">{user?.name?.split(" ").map(n => n[0]).join("").slice(0, 2) || "M"}</span>
          </div>
          <div className="min-w-0">
            <p className="text-white text-sm font-medium truncate">{user?.name || "Member"}</p>
            <p className="text-white/40 text-xs truncate">{user?.email}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-white/70 hover:text-red-400 hover:bg-white/5 transition-colors"
        >
          <LogOut size={18} />Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0">
        <Sidebar />
      </aside>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
              className="lg:hidden fixed inset-0 bg-black/60 z-40"
            />
            <motion.aside
              initial={{ x: -260 }} animate={{ x: 0 }} exit={{ x: -260 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="lg:hidden fixed left-0 top-0 bottom-0 w-64 z-50"
            >
              <Sidebar />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 sm:px-6 shrink-0">
          <div className="flex items-center gap-3">
            <button onClick={() => setMobileOpen(true)} className="lg:hidden text-gray-500 hover:text-black">
              <Menu size={22} />
            </button>
            <h1 className="text-base font-semibold text-black">Member Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <button className="text-gray-500 hover:text-black p-2"><Bell size={18} /></button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center">
                <span className="text-gold text-xs font-bold">{user?.name?.charAt(0) || "M"}</span>
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-medium text-black leading-tight">{user?.name}</p>
                <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 sm:p-6">{children}</main>
      </div>
    </div>
  );
}

export default function MemberLayout({ children }: { children: ReactNode }) {
  const ctx = useLayoutCtx();
  if (ctx.mounted) return <>{children}</>;
  return (
    <LayoutContextProvider>
      <MemberLayoutChrome>{children}</MemberLayoutChrome>
    </LayoutContextProvider>
  );
}
